import axios from 'axios';
import {
  AUTH_LOGIN,
  AUTH_LOGIN_FAILED,
  AUTH_LOGIN_SUCCESS,
  AUTH_LOGIN_NEW_PASSWORD_REQUIRED,
  AUTH_LOGIN_TOTP_REQUIRED,
  AUTH_GET_WORKSPACES_SUCCESS,
} from './types';
import { setSession } from '../../session/actions';
import * as CognitoAuthService from '../../services/CognitoAuthService';
import config from '../../config';

// Holds the CognitoUser instance between the initial authenticateUser call
// and the completeNewPasswordChallenge follow-up. Module-level because the
// instance is non-serializable and shouldn't live in the Redux store.
let pendingChallengeUser = null;

// Exposed for tests / debug only — do not call directly from app code.
export const __unstable = {
  resetPendingChallenge: () => { pendingChallengeUser = null; },
};

export function login(email, password) {
  return async (dispatch) => {
    dispatch({ type: AUTH_LOGIN });
    try {
      const result = await CognitoAuthService.login(email, password);

      if (result.status === 'NEW_PASSWORD_REQUIRED') {
        pendingChallengeUser = result.user;
        dispatch({
          type: AUTH_LOGIN_NEW_PASSWORD_REQUIRED,
          payload: { email, userAttributes: result.userAttributes },
        });
        return;
      }

      if (result.status === 'TOTP_REQUIRED' || result.status === 'MFA_REQUIRED') {
        pendingChallengeUser = result.user;
        dispatch({
          type: AUTH_LOGIN_TOTP_REQUIRED,
          payload: { email, challengeName: result.challengeName },
        });
        return;
      }

      await completeLogin(dispatch, result.session);
    } catch (err) {
      dispatch({
        type: AUTH_LOGIN_FAILED,
        payload: { error: { name: err.name, code: err.code, message: err.message || 'Login failed' } },
      });
    }
  };
}

// Respond to a Cognito MFA challenge after login. Uses the cached CognitoUser
// from the login() call and the challengeName stored in Redux state so that
// both SOFTWARE_TOKEN_MFA and SMS_MFA routes use the correct mfaType.
export function sendCognitoMFACode(code) {
  return async (dispatch, getState) => {
    if (!pendingChallengeUser) {
      dispatch({
        type: AUTH_LOGIN_FAILED,
        payload: { error: { message: 'Session expired, please sign in again' } },
      });
      return;
    }
    try {
      const { challengeName } = getState().user.cognitoTotpChallenge;
      const result = await CognitoAuthService.sendMFACode(pendingChallengeUser, code, challengeName || 'SOFTWARE_TOKEN_MFA');
      pendingChallengeUser = null;
      await completeLogin(dispatch, result.session);
    } catch (err) {
      dispatch({
        type: AUTH_LOGIN_FAILED,
        payload: { error: { name: err.name, code: err.code, message: err.message || 'Invalid code' } },
      });
    }
  };
}

export function completeNewPassword(newPassword, userAttributes = {}) {
  return async (dispatch) => {
    if (!pendingChallengeUser) {
      dispatch({
        type: AUTH_LOGIN_FAILED,
        payload: { error: { message: 'Session expired, please sign in again' } },
      });
      return;
    }
    try {
      const result = await CognitoAuthService.completeNewPasswordChallenge(
        pendingChallengeUser,
        newPassword,
        userAttributes,
      );
      pendingChallengeUser = null;

      if (result.status === 'MFA_REQUIRED' || result.status === 'TOTP_REQUIRED') {
        // After completing the new-password challenge Cognito can chain into
        // an MFA challenge if the user pool requires it. Store the user and
        // surface the same TOTP UI as a fresh login.
        pendingChallengeUser = result.user;
        dispatch({
          type: AUTH_LOGIN_TOTP_REQUIRED,
          payload: { challengeName: result.challengeName },
        });
        return;
      }

      await completeLogin(dispatch, result.session);
    } catch (err) {
      dispatch({
        type: AUTH_LOGIN_FAILED,
        payload: { error: { name: err.name, code: err.code, message: err.message || 'Failed to set new password' } },
      });
    }
  };
}

// Bootstrap the Redux session after a successful Cognito authentication:
//   1. Use the access token immediately for the initial bootstrap calls.
//   2. Fetch workspaces (tenant-agnostic — backend resolves by cognitoSub).
//   3. Pick a default tenant (first workspace; the tenant switcher UI lands later).
//   4. Fetch /auth/me with X-Tenant-Id to get the inflated user.
//   5. Persist the final session and emit AUTH_LOGIN_SUCCESS.
async function completeLogin(dispatch, session) {
  const accessToken = session.getAccessToken().getJwtToken();
  const refreshToken = session.getRefreshToken().getToken();

  // The just-issued access token isn't in Redux yet, so the store.js axios
  // interceptor can't inject it — pass it explicitly on the bootstrap calls.
  const authHeader = { Authorization: `Bearer ${accessToken}` };

  const workspacesResponse = await axios.get(`${config.apiBaseUrl}/su/auth/get-workspaces`, {
    headers: authHeader,
  }).catch((err) => {
    if (err.response?.status === 404) return { data: { workspaces: [] } };
    throw err;
  });

  // Support both new { workspaces, predefinedTenant } and legacy array format
  const responseData = workspacesResponse.data || {};
  const workspaces = Array.isArray(responseData) ? responseData : (responseData.workspaces || []);
  const predefinedTenant = Array.isArray(responseData) ? null : (responseData.predefinedTenant || null);

  if (workspaces.length === 0) {
    dispatch({
      type: AUTH_LOGIN_FAILED,
      payload: { error: { message: 'No workspaces available for this account' } },
    });
    return;
  }

  // Use the predefined tenant if it matches an available workspace, otherwise fall back to the first
  const defaultWorkspace = (predefinedTenant && workspaces.find(ws => ws.tenant === predefinedTenant)) || workspaces[0];
  const { tenant } = defaultWorkspace;

  const meResponse = await axios.get(`${config.apiBaseUrl}/su/auth/me`, {
    headers: { ...authHeader, 'X-Tenant-Id': tenant },
  });
  const user = meResponse.data;

  const finalSession = { token: accessToken, refreshToken, tenant, user };
  dispatch(setSession(finalSession));
  dispatch({ type: AUTH_LOGIN_SUCCESS, payload: { ...finalSession, workspaces } });
  dispatch({ type: AUTH_GET_WORKSPACES_SUCCESS, payload: workspaces });
}

